import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import SubImportMenuItem from './SubImportMenuItem'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'

import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext, DialogsProvider } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'

import axios from 'axios'
import qs from 'qs'

import { slugifyString, convertGPTResponsesToArray } from '../../utils/index'
import AdvancedImportSettings from './AdvancedImportSettings'
import ImportingNotice from './ImportingNotice'
import { useSimplifiedImportAtMount } from '../../hooks/useSimplifiedImportAtMount'
import { importErrorMessage } from './importError'

const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore

const steps = ['Source', 'Settings', 'Processing']

export default function TextEditorImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const { label, location } = usePageInfo()

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [doNotSaveGraph, setDoNotSaveGraph] = React.useState(false)

    const [fromInsideContext, setFromInsideContext] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const { username } = React.useContext(UserContext)
    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)

    React.useEffect(() => {
        if (location == 'context' && label) {
            setFromInsideContext(true)
        }
    }, [label])

    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const simplifyImport = useSimplifiedImportAtMount()

    const [activeStep, setActiveStep] = React.useState(1)
    const [skipped, setSkipped] = React.useState(new Set())

    const isStepOptional = step => {
        return step === 1
    }

    const isStepSkipped = step => {
        return skipped.has(step)
    }

    const handleNext = () => {
        let newSkipped = skipped
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
        }

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(1)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    const submitDataForm = () => {
        const redirectUrl =
            fromInsideContext && doNotSaveGraph
                ? ``
                : `/${username}/${selectedContext}/edit`

        const dataToSend = {
            context: selectedContext,
            addedContexts: selectedContext,
            entry: { body: searchString },
            redirectUrl: redirectUrl,
            doNotSaveGraph: doNotSaveGraph,
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post(`/post`, dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(
                            typeof response.data.error === 'object'
                                ? JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setActiveStep(1)
                    }
                    if (response.data.entryuid) {
                        const postedby = username
                        const res = response.data

                        // TODO REFACTOR
                        // we are first generating the statmeents and then sending an event to the graph to add statements,
                        // seems a bit dirty
                        // Handle private mode statements
                        if (doNotSaveGraph && res.statements) {
                            // Private mode: add statements to Zustand store without saving to DB
                            const entryuids = JSON.parse(res.entryuid)
                            const statementHashtags =
                                res.statementHashtags || {}
                            addPrivateStatementsToStore(
                                res.statements,
                                selectedContext,
                                entryuids,
                                statementHashtags
                            )
                        }

                        if (
                            window.myEventEmitter &&
                            myEventEmitter._events['chatMessage']
                        ) {
                            myEventEmitter.emit('chatMessage', {
                                source: null,
                                postedby: postedby,
                                entryuid: JSON.parse(res.entryuid),
                                entrytext: JSON.parse(res.entrytext),
                                entryname: JSON.parse(res.entryname),
                                tags: JSON.parse(res.tags),
                                timestamp: JSON.parse(res.timestamp),
                                graph: res.graph,
                                stimulateVariability: false,
                                message: res.message,
                                doNotSaveGraph: doNotSaveGraph,
                                replaceEntities: replaceEntities,
                                statementHashtags: res.statementHashtags || {},
                                modifyAnalyzedText: modifyAnalyzedText,
                            })
                            closeImportDialog()
                        }
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(importErrorMessage(error))
            })
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleNumberOfResultsChange = event => {
        setNumberOfResults(event.target.value)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    const handleSearchLanguageChange = event => {
        setSearchLanguage(event.target.value)
    }

    const handleSearchCountryChange = event => {
        setSearchCountry(event.target.value)
    }

    const handleAiTopicsClick = React.useCallback(() => {
        setActiveSubImportDialog(false)
        setActiveImportDialog('aiGraphs')
    }, [])

    React.useEffect(() => {
        switch (activeStep) {
            case 0:
                setActiveImportDialog('import')
                break
            case 1:
                if (!presetContext) {
                    setSelectedContext(generatePresetContext())
                }

                break
            case 2:
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from AI for.`
                    )
                    setActiveStep(1)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(1)
                    break
                }
                if (selectedContext) {
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
                setActiveStep(1)
                break
        }
    }, [activeStep])

    const handleStep = step => {
        setActiveStep(step)
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Discover the main ideas and gaps in any text'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {/* A stepper whose labels submit on click would be the most prominent control left. */}
                {!simplifyImport && (
                    <Stepper
                        activeStep={activeStep}
                        sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                    >
                        {steps.map((label, index) => {
                            const stepProps = {}
                            const labelProps = {}
                            // if (isStepOptional(index)) {
                            //     labelProps.optional = (
                            //         <Typography variant="caption">
                            //             Optional
                            //         </Typography>
                            //     )
                            // }
                            if (isStepSkipped(index)) {
                                stepProps.completed = false
                            }
                            return (
                                <Step key={label} {...stepProps}>
                                    <StepButton
                                        onClick={() => handleStep(index)}
                                        completed={(
                                            completedSteps.indexOf(index) >= 0
                                        ).toString()}
                                    >
                                        <StepLabel
                                            {...labelProps}
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    fontSize: {
                                                        xs: '0.75rem',
                                                        sm: 'initial',
                                                    },
                                                },
                                            }}
                                        >
                                            {label}
                                        </StepLabel>
                                    </StepButton>
                                </Step>
                            )
                        })}
                    </Stepper>
                )}

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 1 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <DialogContentText>
                                Discover the main topics, recurrent themes, and
                                missing connections in any text or an article:
                            </DialogContentText>
                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={simplifyImport ? 12 : 7}>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <TextField
                                            label={'Paste Your Text Here'}
                                            value={searchString}
                                            variant="outlined"
                                            onChange={handleSearchStringChange}
                                            aria-describedby="searchString-helperText"
                                            multiline
                                            rows={8}
                                            id="reactTextEditor"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                    '& .MuiInputBase-inputMultiline': {
                                                        boxShadow:
                                                            'none !important',
                                                        border:
                                                            'none !important',
                                                    },
                                                },
                                            }}
                                        />
                                    </FormControl>
                                    {searchString &&
                                        searchString.trim().length > 0 &&
                                        searchString.trim().length < 300 && (
                                            <Typography
                                                variant="caption"
                                                color="warning.main"
                                                sx={{ mt: 1 }}
                                            >
                                                Your text for analysis seems to
                                                be short. If you mean to send an
                                                AI prompt to generate a graph,
                                                please, consider using the{' '}
                                                <Link
                                                    component="button"
                                                    variant="caption"
                                                    color="warning.main"
                                                    sx={{
                                                        verticalAlign:
                                                            'baseline',
                                                    }}
                                                    onClick={() => {
                                                        setActiveImportDialog(
                                                            'aiImport'
                                                        )
                                                    }}
                                                >
                                                    AI generation import
                                                </Link>{' '}
                                                instead.
                                            </Typography>
                                        )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            marginTop: 4,
                                        }}
                                    >
                                        <Typography variant="body1">
                                            or
                                        </Typography>
                                        {[
                                            {
                                                key: 'files',
                                                label: 'Upload Text Files',
                                                Icon: UploadFileOutlinedIcon,
                                                color: 'primary',
                                                onClick: () =>
                                                    setActiveImportDialog(
                                                        'textFilesImport'
                                                    ),
                                            },
                                            {
                                                key: 'aiGraph',
                                                label:
                                                    'Use as AI Prompt to Generate a Graph',
                                                Icon: AutoAwesomeOutlinedIcon,
                                                color: 'info',
                                                onClick: handleAiTopicsClick,
                                            },
                                        ].map(
                                            ({
                                                key,
                                                label,
                                                Icon,
                                                color,
                                                onClick,
                                            }) => (
                                                <Button
                                                    key={key}
                                                    variant="contained"
                                                    color={color}
                                                    startIcon={<Icon />}
                                                    onClick={onClick}
                                                    sx={{
                                                        borderRadius: '999px',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        px: 2.5,
                                                        py: 1,
                                                    }}
                                                >
                                                    {label}
                                                </Button>
                                            )
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={simplifyImport ? 12 : 5}>
                                    <AdvancedImportSettings>
                                        <FormControl
                                            fullWidth
                                            sx={{ marginTop: 4 }}
                                        >
                                            <EditableSelect
                                                selectItems={contextNames}
                                                label={'Import To'}
                                                initialValue={selectedContext}
                                                onChange={name =>
                                                    setSelectedContext(name)
                                                }
                                                aria-describedby="contextName-helperText"
                                                id="contextName"
                                            />
                                            <FormHelperText
                                                id="contextName-helperText"
                                                sx={{ marginTop: 2 }}
                                            >
                                                Please, choose the name of the
                                                graph to import your content to
                                                and click enter.
                                            </FormHelperText>
                                        </FormControl>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 4,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="modifyAnalyzedText-label">
                                                Build a knowledge graph from
                                            </InputLabel>
                                            <Select
                                                labelId="modifyAnalyzedText-label"
                                                id="modifyAnalyzedText"
                                                value={modifyAnalyzedText}
                                                onChange={event =>
                                                    setModifyAnalyzedText(
                                                        event.target.value
                                                    )
                                                }
                                                label="Build a knowledge graph from"
                                            >
                                                <MenuItem
                                                    value={'none'}
                                                    key="1"
                                                >
                                                    the words used in text
                                                </MenuItem>
                                                <MenuItem
                                                    value={'detectEntities'}
                                                    key="2"
                                                >
                                                    the words and detected
                                                    entities
                                                </MenuItem>
                                                <MenuItem
                                                    value={
                                                        'extractEntitiesOnly'
                                                    }
                                                    key="3"
                                                >
                                                    only the detected entities
                                                </MenuItem>
                                            </Select>
                                            {modifyAnalyzedText != 'none' && (
                                                <FormHelperText
                                                    sx={{ marginTop: 1 }}
                                                >
                                                    By default, we extract the
                                                    words used in your text. You
                                                    can also choose to detect
                                                    and tag entities in your
                                                    text and build a graph from
                                                    them.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                        {modifyAnalyzedText != 'none' && (
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 2,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            id="replaceEntities"
                                                            checked={
                                                                replaceEntities
                                                            }
                                                            onChange={event =>
                                                                setReplaceEntities(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Replace entities with root concepts"
                                                />
                                                {replaceEntities && (
                                                    <FormHelperText
                                                        id="replaceEntities-helperText"
                                                        sx={{ marginTop: 1 }}
                                                    >
                                                        When turned on, we will
                                                        not only detect the
                                                        entities, but also
                                                        replace them with their
                                                        ontological root
                                                        concepts throughout your
                                                        text.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    </AdvancedImportSettings>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}
                {activeStep === 2 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                    {/* {isStepOptional(activeStep) && (
                        <Button
                            color="inherit"
                            onClick={handleSkip}
                            sx={{ mr: 1 }}
                        >
                            Skip
                        </Button>
                    )} */}
                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString.length > 0}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {simplifyImport
                            ? 'Start processing'
                            : activeStep === steps.length - 1
                            ? 'Visualize'
                            : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `text_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `text_${twoFirstSearchQueryWords}`
}
